import { GEOMETRY_SCHEMA_VERSION } from '@gatecheckpro/contracts';
import type {
  CustomerTopology,
  VenueMapTopology,
  VenueMapVersionTopology,
} from '@gatecheckpro/contracts';

/** Turns the identifier-keyed view the server sends into the document it takes. */
export function roomTopologyOf(view: VenueMapVersionTopology): VenueMapTopology {
  const sectionCodeById = new Map(
    view.sections.map((section) => [section.sectionId, section.code]),
  );
  const rowCodeById = new Map(
    view.sections.flatMap((section) => section.rows.map((row) => [row.rowId, row.code] as const)),
  );
  const tableCodeById = new Map(
    view.sections.flatMap((section) =>
      section.tables.map((table) => [table.tableId, table.code] as const),
    ),
  );
  const seatKeyById = new Map(view.seats.map((seat) => [seat.seatId, seat.seatKey]));

  return {
    schemaVersion: GEOMETRY_SCHEMA_VERSION,
    canvas: view.version.canvas,
    sections: view.sections.map((section) => ({
      code: section.code,
      displayName: section.displayName,
      kind: section.kind,
      displayOrder: section.displayOrder,
      geometry: section.geometry,
      rows: section.rows.map((row) => ({
        code: row.code,
        displayName: row.displayName,
        displayOrder: row.displayOrder,
        geometry: row.geometry,
      })),
      tables: section.tables.map((table) => ({
        code: table.code,
        displayName: table.displayName,
        seatCapacity: table.seatCapacity,
        displayOrder: table.displayOrder,
        geometry: table.geometry,
      })),
    })),
    seats: view.seats.map((seat) => ({
      seatKey: seat.seatKey,
      seatLabel: seat.seatLabel,
      sectionCode: sectionCodeById.get(seat.sectionId) ?? '',
      rowCode: seat.rowId === null ? null : (rowCodeById.get(seat.rowId) ?? null),
      tableCode: seat.tableId === null ? null : (tableCodeById.get(seat.tableId) ?? null),
      isAccessible: seat.isAccessible,
      hasObstructedView: seat.hasObstructedView,
      displayOrder: seat.displayOrder,
      geometry: seat.geometry,
    })),
    seatGroups: view.seatGroups.map((group) => ({
      code: group.code,
      displayName: group.displayName,
      kind: group.kind,
      displayOrder: group.displayOrder,
      geometry: group.geometry,
      seatKeys: group.seatIds.map((id) => seatKeyById.get(id) ?? '').filter((key) => key !== ''),
    })),
    // Companion pairs travel by seat key, like every other cross-reference in
    // the authoring document. A pair naming a chair this version does not have
    // is dropped rather than emitted half-resolved.
    companions: view.companions
      .map((pair) => ({
        accessibleSeatKey: seatKeyById.get(pair.accessibleSeatId) ?? '',
        companionSeatKey: seatKeyById.get(pair.companionSeatId) ?? '',
      }))
      .filter((pair) => pair.accessibleSeatKey !== '' && pair.companionSeatKey !== ''),
  };
}

/** An empty room. A document must carry at least one section to be importable. */

/**
 * The same drawing, from what a customer is allowed to know about the room.
 *
 * A second projection rather than a second renderer. The customer read is
 * identifier-keyed and carries display names where the organizer's carries
 * authoring codes — there are no codes on it at all, deliberately, because an
 * authoring code is the organizer's private handle for a section.
 *
 * So identifiers become the drawing's keys. That is not a workaround: for this
 * surface the key that matters *is* `venueMapSeatId`, because it is what a seat
 * option is keyed on and what a hold is requested with. A chair a customer
 * clicks resolves to exactly the identifier the server will be asked about,
 * with no lookup table in between to get wrong.
 */
export function roomTopologyOfCustomer(topology: CustomerTopology): VenueMapTopology {
  return {
    schemaVersion: topology.geometrySchemaVersion,
    canvas: topology.canvas,
    sections: topology.sections.map((section) => ({
      code: section.sectionId,
      displayName: section.displayName,
      kind: section.kind,
      displayOrder: section.displayOrder,
      geometry: section.geometry,
      rows: section.rows.map((row) => ({
        code: row.rowId,
        displayName: row.displayName,
        displayOrder: row.displayOrder,
        geometry: row.geometry,
      })),
      tables: section.tables.map((table) => ({
        code: table.tableId,
        displayName: table.displayName,
        seatCapacity: table.seatCapacity,
        displayOrder: table.displayOrder,
        geometry: table.geometry,
      })),
    })),
    seats: topology.seats.map((seat) => ({
      seatKey: seat.venueMapSeatId,
      seatLabel: seat.seatLabel,
      sectionCode: seat.sectionId,
      rowCode: seat.rowId,
      tableCode: seat.tableId,
      isAccessible: seat.isAccessible,
      hasObstructedView: seat.hasObstructedView,
      displayOrder: seat.displayOrder,
      geometry: seat.geometry,
    })),
    seatGroups: [],
    /*
     * The pairs, unchanged.
     *
     * The customer read is keyed by occurrence and so is the drawing, so this
     * is a rename rather than a resolution — which is the point: there is no
     * step here where a client could get a link wrong, because there is no
     * lookup. A companion relationship is authored, and a renderer that
     * derived one from adjacency would be inventing a fact about a room it has
     * never been in.
     */
    companions: topology.companions.map((pair) => ({
      accessibleSeatKey: pair.accessibleSeatId,
      companionSeatKey: pair.companionSeatId,
    })),
  };
}
